import React, { useEffect, useRef, useState } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap"
import { enviarCodigoConfirmacaoEmail, confirmarCodigoEmail } from "../services/confirmacaoEmail"
import "./ConfirmarEmailModal.css"

const CODIGO_TAMANHO = 7

const getErrorMessage = (error) => (
    error?.response?.data?.detail ||
    error?.response?.data?.email?.[0] ||
    error?.message ||
    "Ocorreu um erro. Tente novamente."
)

const ConfirmarEmailModal = ({ isOpen, email, toggle, onConfirmado, onContinuarSemConfirmar }) => {
    const [etapa, setEtapa] = useState("pergunta")
    const [codigoDigits, setCodigoDigits] = useState(Array(CODIGO_TAMANHO).fill(""))
    const [erro, setErro] = useState("")
    const [enviando, setEnviando] = useState(false)
    const [validando, setValidando] = useState(false)
    const [cooldownReenvio, setCooldownReenvio] = useState(0)

    const codigoRefs = useRef(Array.from({ length: CODIGO_TAMANHO }, () => React.createRef()))
    const cooldownInterval = useRef(null)

    const iniciarCooldown = () => {
        if (cooldownInterval.current) clearInterval(cooldownInterval.current)
        setCooldownReenvio(60)
        cooldownInterval.current = setInterval(() => {
            setCooldownReenvio((atual) => {
                if (atual <= 1) {
                    clearInterval(cooldownInterval.current)
                    return 0
                }
                return atual - 1
            })
        }, 1000)
    }

    const enviarCodigo = async () => {
        setErro("")
        setEnviando(true)
        try {
            await enviarCodigoConfirmacaoEmail(email)
            iniciarCooldown()
        } catch (error) {
            setErro(getErrorMessage(error))
        } finally {
            setEnviando(false)
        }
    }

    const iniciarConfirmacao = async () => {
        setEtapa("codigo")
        await enviarCodigo()
    }

    useEffect(() => {
        if (isOpen && email) {
            setEtapa("pergunta")
            setCodigoDigits(Array(CODIGO_TAMANHO).fill(""))
            setErro("")
        }

        return () => {
            if (cooldownInterval.current) clearInterval(cooldownInterval.current)
        }
    }, [isOpen, email])

    const handleDigitChange = (index, e) => {
        if (validando) return

        const valor = e.target.value.slice(-1)
        const novosDigitos = [...codigoDigits]
        novosDigitos[index] = valor
        setCodigoDigits(novosDigitos)

        if (valor && index < CODIGO_TAMANHO - 1) {
            codigoRefs.current[index + 1].current?.focus()
        }
    }

    const handleDigitKeyDown = (index, e) => {
        if (e.key === "Backspace" && !codigoDigits[index] && index > 0) {
            codigoRefs.current[index - 1].current?.focus()
        } else if (e.key === "Enter") {
            confirmarCodigo()
        }
    }

    const handleDigitPaste = (index, e) => {
        e.preventDefault()
        const colado = (e.clipboardData || window.clipboardData).getData("text").trim()
        if (!colado) return

        const caracteres = colado.split("")
        const novosDigitos = [...codigoDigits]

        for (let i = 0; i < caracteres.length && (index + i) < CODIGO_TAMANHO; i++) {
            novosDigitos[index + i] = caracteres[i]
        }

        setCodigoDigits(novosDigitos)

        const ultimoIndex = Math.min(index + caracteres.length, CODIGO_TAMANHO) - 1
        if (ultimoIndex >= 0) codigoRefs.current[ultimoIndex].current?.focus()
    }

    const confirmarCodigo = async () => {
        if (validando) return

        setErro("")
        setValidando(true)

        try {
            await confirmarCodigoEmail(email, codigoDigits.join(""))
            onConfirmado?.(email)
        } catch (error) {
            setErro(getErrorMessage(error))
        } finally {
            setValidando(false)
        }
    }

    const reenviarCodigo = () => {
        if (enviando || cooldownReenvio > 0) return
        enviarCodigo()
    }

    if (etapa === "pergunta") {
        return (
            <Modal isOpen={isOpen} toggle={toggle}>
                <ModalHeader toggle={toggle}>Confirmar E-mail</ModalHeader>
                <ModalBody>
                    <div className="texto-instrucao-confirmar-email">
                        O e-mail <strong>{email}</strong> ainda não foi confirmado. Deseja confirmar agora? Um código de verificação será enviado para esse endereço.
                    </div>

                    {erro && <div className="erro-confirmar-email">{erro}</div>}
                </ModalBody>
                <ModalFooter className="rodape-botoes-confirmar-email">
                    <button className="hdv-btn-forms hdv-btn-default" onClick={toggle}>Cancelar</button>
                    {onContinuarSemConfirmar && (
                        <button
                            className="hdv-btn-forms hdv-btn-yellow"
                            onClick={() => onContinuarSemConfirmar(email)}>
                            Continuar sem confirmar
                        </button>
                    )}
                    <button
                        className="hdv-btn-forms hdv-btn-green"
                        disabled={enviando}
                        onClick={iniciarConfirmacao}>
                        {enviando ? "Enviando..." : "Enviar código"}
                    </button>
                </ModalFooter>
            </Modal>
        )
    }

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>Confirmar E-mail</ModalHeader>
            <ModalBody>
                <div className="texto-instrucao-confirmar-email">
                    Enviamos um código de verificação para <strong>{email}</strong>. Digite-o abaixo para confirmar este e-mail.
                </div>

                <div className="codigo-input-group-confirmar-email">
                    {codigoDigits.map((digito, index) => (
                        <input
                            key={index}
                            ref={codigoRefs.current[index]}
                            type="text"
                            inputMode="text"
                            maxLength={1}
                            autoComplete="off"
                            autoFocus={index === 0}
                            disabled={validando}
                            value={digito}
                            onChange={(e) => handleDigitChange(index, e)}
                            onKeyDown={(e) => handleDigitKeyDown(index, e)}
                            onPaste={(e) => handleDigitPaste(index, e)}
                        />
                    ))}
                </div>

                {erro && <div className="erro-confirmar-email">{erro}</div>}

                <div className="reenviar-codigo-confirmar-email">
                    {cooldownReenvio > 0
                        ? <span>Reenviar código em {cooldownReenvio}s</span>
                        : <a href="#" onClick={(e) => { e.preventDefault(); reenviarCodigo() }}>{enviando ? "Reenviando..." : "Reenviar Código"}</a>
                    }
                </div>
            </ModalBody>
            <ModalFooter className="rodape-botoes-confirmar-email">
                <button className="hdv-btn-forms hdv-btn-default" onClick={toggle}>Cancelar</button>
                {onContinuarSemConfirmar && (
                    <button
                        className="hdv-btn-forms hdv-btn-yellow"
                        disabled={validando}
                        onClick={() => onContinuarSemConfirmar(email)}>
                        Continuar sem confirmar
                    </button>
                )}
                <button
                    className="hdv-btn-forms hdv-btn-green"
                    disabled={validando || codigoDigits.some((d) => !d)}
                    onClick={confirmarCodigo}>
                    {validando ? "Confirmando..." : "Confirmar"}
                </button>
            </ModalFooter>
        </Modal>
    )
}

export default ConfirmarEmailModal
